import * as ex from "excalibur";
import { CONFIG } from "../config";
import { CityMap } from "../map/CityMap";
import { Building, setGrayscaleEnabled } from "../actors/Building";
import { TileChunkManager } from "../graphics/BackgroundCanvas";
import { createTapHandler, TAP_SLOP_PX } from "../input/tap";
import { MAP_CENTER, START_RADIUS, SPECIAL_BUILDINGS, TERRAIN, isHttpUrl, PLACEMENT_ERRORS, } from "@dein-go-city/shared";
import { updateLeaderboard, hideLeaderboard, setLeaderboardRowClickHandler, } from "../ui/Leaderboard";
import { updateResourceHud, setBuildHandler, hideResourceHud, } from "../ui/ResourceHud";
import { showBuildMenu, showPlacementBanner, hidePlacementBanner, showPlacementErrorToast, } from "../ui/BuildMenu";
import { showDestroyConfirm } from "../ui/DestroyConfirmModal";
import { hideBuildingTooltip } from "../ui/BuildingTooltip";
import { showBuildingInfoOverlay, hideBuildingInfoOverlay, } from "../ui/BuildingInfoOverlay";
import { setShowMyBuildingsHandler } from "../ui/UserInfoModal";
import { updateAddCompanyQuota, setOnCompanyCreated, } from "../ui/AddCompanyModal";
import { showUserBuildingsPanel, hideUserBuildingsPanel, } from "../ui/UserBuildingsPanel";
import { isMobile } from "../ui/platform";
import { isAccessibilityMode } from "../ui/accessibility";
import { initCursor, setCursor, setPlacementActive } from "../ui/cursor";
import { BuildDustEffect } from "../graphics/BuildDustEffect";
import { TileHighlight } from "../graphics/TileHighlight";
import { playBuildingRedirect, playBuildingPlaced } from "../audio/sfx";
import posthog from "../posthog";
import { apiPost, apiDelete, ApiError } from "../api";
import { MapStore } from "./MapStore";
// At maximum pan the buildable border takes up BUF_FRAC of the viewport dimension.
const BUF_FRAC = 0.15;
// Hard floor on how far out the camera may ever zoom, regardless of screen size or how large
// mapRadius has grown. Without it, calcMinZoom() can return an arbitrarily small value on a
// wide/large monitor viewing a maximally-grown city — and TileChunkManager's chunk grid is
// unbounded by design (see BackgroundCanvas.ts), so a low enough zoom asks it to rasterize
// thousands of chunks at once and stalls the renderer. Holding Q reaches whatever the
// computed floor is within a couple of seconds, so that floor must be a real, safe bound.
// Lives in CONFIG (not a local const) since BackgroundCanvas.ts's chunk-per-axis backstop is
// derived from this same value — see its MAX_CHUNKS_PER_AXIS comment.
const ABSOLUTE_MIN_ZOOM = CONFIG.ABSOLUTE_MIN_ZOOM;
// WASD pan speed in screen pixels/sec at zoom 1 (matches the feel of a mouse-drag pan).
const KEY_PAN_SPEED_PX_PER_SEC = 900;
// Q/E zoom rate — multiplicative factor applied per second of holding the key.
const KEY_ZOOM_RATE_PER_SEC = 1.2;
// WASD/Q/E must not hijack typing in a text field (e.g. the account name/password forms).
function isTypingTarget() {
    const el = document.activeElement;
    return (el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement ||
        (el instanceof HTMLElement && el.isContentEditable));
}
// A freshly placed building only appears once it round-trips through the server and comes
// back over SSE — show construction dust for at least this long so a fast round-trip doesn't
// look like a flicker, and stop waiting after this long even if the building never arrives
// (should only happen if a broadcast is somehow missed).
const DUST_MIN_VISIBLE_MS = 3000;
const DUST_FALLBACK_MS = 15000;
export class GameScene extends ex.Scene {
    _currentUser;
    readOnly;
    eventsUrl;
    cityMap;
    chunkManager;
    _buildings = new Map();
    mapStore = new MapStore();
    _mapRadius = -1; // -1 = not yet received; forces first syncMapState to update bounds
    _viewBounds;
    _minZoom = 1;
    _placing = null;
    // Set by focusNewBuilding when a just-created manual company hasn't appeared as an actor yet
    // (it arrives over SSE, asynchronously) — addBuildingActor checks this and focuses on arrival.
    _pendingFocusId = null;
    // Keyed by "x,y" — tracks dust effects for tiles awaiting their placement to arrive over SSE.
    _pendingDust = new Map();
    activePointers = new Map();
    // Held direction from the on-screen accessibility pan/zoom buttons (AccessibilityControls.ts),
    // fed into updateKeyboardCamera alongside WASD/Q/E so both input sources share one speed/zoom
    // -rate/clamping implementation instead of duplicating it.
    _virtualPanDir = ex.vec(0, 0);
    _virtualZoomDir = 0;
    setVirtualPan(dx, dy) {
        this._virtualPanDir = ex.vec(dx, dy);
    }
    setVirtualZoom(dir) {
        this._virtualZoomDir = dir;
    }
    constructor(_currentUser, readOnly = true, eventsUrl = "/api/map/events") {
        super();
        this._currentUser = _currentUser;
        this.readOnly = readOnly;
        this.eventsUrl = eventsUrl;
    }
    async onInitialize(engine) {
        this.cityMap = new CityMap();
        initCursor(engine.canvas);
        this.add(this.cityMap.isoMap);
        // getTileByPoint (used by CityMap.attachClickHandling and the hover-cursor preview
        // below) survives kill() — it's the same lookup IsometricMap's own tile-dispatch uses.
        this.chunkManager = new TileChunkManager(TERRAIN);
        this.cityMap.killAllTiles();
        this.camera.pos = this.cityMap.getWorldCenter();
        this.setupCameraControls(engine);
        setLeaderboardRowClickHandler((userId, name) => this.showBuildingsPanelFor(userId, name));
        if (this._currentUser) {
            setShowMyBuildingsHandler(() => this.showBuildingsPanelFor(this._currentUser.id, this._currentUser.name));
        }
        if (!this.readOnly && this._currentUser) {
            this.setupBuildMode(engine);
            setOnCompanyCreated((id) => this.focusNewBuilding(id));
        }
        this.mapStore.connect(this.eventsUrl, (state) => this.syncMapState(state, engine));
    }
    onDeactivate() {
        this.activePointers.clear();
        this.mapStore.disconnect();
        for (const { fallback } of this._pendingDust.values())
            clearTimeout(fallback);
        this._pendingDust.clear();
        hideLeaderboard();
        hideResourceHud();
        hideBuildingInfoOverlay();
        hideUserBuildingsPanel();
        setLeaderboardRowClickHandler(null);
        setShowMyBuildingsHandler(null);
        setOnCompanyCreated(null);
    }
    onPreUpdate(engine, delta) {
        this.chunkManager.update(this, this.camera, engine);
        this.updateKeyboardCamera(engine, delta);
    }
    updateKeyboardCamera(engine, delta) {
        if (isTypingTarget())
            return;
        const keyboard = engine.input.keyboard;
        const dt = delta / 1000;
        let dx = this._virtualPanDir.x;
        let dy = this._virtualPanDir.y;
        if (keyboard.isHeld(ex.Keys.W))
            dy -= 1;
        if (keyboard.isHeld(ex.Keys.S))
            dy += 1;
        if (keyboard.isHeld(ex.Keys.A))
            dx -= 1;
        if (keyboard.isHeld(ex.Keys.D))
            dx += 1;
        if (dx !== 0 || dy !== 0) {
            const len = Math.hypot(dx, dy);
            const step = (KEY_PAN_SPEED_PX_PER_SEC * dt) / this.camera.zoom / len;
            this.camera.pos.x += dx * step;
            this.camera.pos.y += dy * step;
        }
        if (keyboard.isHeld(ex.Keys.Q) || this._virtualZoomDir < 0) {
            this.camera.zoom = Math.max(this._minZoom, this.camera.zoom / (1 + KEY_ZOOM_RATE_PER_SEC * dt));
        }
        if (keyboard.isHeld(ex.Keys.E) || this._virtualZoomDir > 0) {
            this.camera.zoom = Math.min(2, this.camera.zoom * (1 + KEY_ZOOM_RATE_PER_SEC * dt));
        }
    }
    worldBoundsForRadius(radius) {
        const tw = CONFIG.TILE_WIDTH;
        const th = CONFIG.TILE_HEIGHT;
        const o = MAP_CENTER - radius;
        const s = 2 * radius + 1;
        return {
            left: -s * (tw / 2),
            right: s * (tw / 2),
            top: 2 * o * (th / 2),
            bottom: (2 * o + 2 * s - 2) * (th / 2) + th,
        };
    }
    calcMinZoom(engine) {
        const { left, right, top, bottom } = this._viewBounds;
        const bw = right - left;
        const bh = bottom - top;
        const fitZoom = ((1 - 2 * BUF_FRAC) *
            Math.max(engine.screen.drawWidth / bw, engine.screen.drawHeight / bh)) /
            0.85;
        return Math.max(fitZoom, ABSOLUTE_MIN_ZOOM);
    }
    syncMapState(state, engine) {
        this.chunkManager.setStreets(this.mapStore.getStreetKeys());
        setGrayscaleEnabled(state.grayscaleManualBuildings);
        // Update camera bounds when mapRadius changes (grows or shrinks)
        if (state.mapRadius !== this._mapRadius) {
            this._mapRadius = state.mapRadius;
            this._viewBounds = this.worldBoundsForRadius(state.mapRadius);
            this._minZoom = this.calcMinZoom(engine);
            // On map shrink, clamp zoom up so camera stays valid
            if (this.camera.zoom < this._minZoom) {
                this.camera.zoom = this._minZoom;
            }
        }
        const incoming = new Map(state.buildings.map((b) => [b.id, b]));
        // Remove actors no longer in the incoming state
        for (const [id, entry] of this._buildings) {
            if (!incoming.has(id)) {
                entry.actor.kill();
                this.cityMap.releaseTile(entry.tile);
                this._buildings.delete(id);
            }
        }
        updateLeaderboard(state.leaderboard, this._currentUser?.id);
        if (this._currentUser) {
            updateResourceHud(state.resources, this._currentUser.id);
        }
        if (this._currentUser && !this.readOnly) {
            const ownManualCount = this.mapStore.manualBuildingCountForUser(this._currentUser.id);
            const remaining = state.manualBuildingLimit === null
                ? Infinity
                : state.manualBuildingLimit - ownManualCount;
            updateAddCompanyQuota(state.manualBuildingLimit, remaining);
        }
        // Add new actors; re-create changed ones (updatedAt differs → building was edited)
        for (const building of state.buildings) {
            const existing = this._buildings.get(building.id);
            if (existing) {
                if (existing.updatedAt !== building.updatedAt) {
                    existing.actor.kill();
                    this.cityMap.releaseTile(existing.tile);
                    this._buildings.delete(building.id);
                    this.addBuildingActor(building);
                }
            }
            else {
                this.addBuildingActor(building);
            }
        }
    }
    addBuildingActor(building) {
        const tile = this.cityMap.isoMap.getTile(building.tileX, building.tileY);
        if (!tile)
            return;
        this.cityMap.occupyTile(tile);
        this.resolvePendingDust(building.tileX, building.tileY);
        const canDestroy = !this.readOnly &&
            !!this._currentUser &&
            building.ownerId === this._currentUser.id &&
            (building.kind === "special" ||
                (building.kind === "company" && building.source === "manual"));
        // Only pass the user id when destroy is actually possible (build mode active) so the
        // "tap to destroy" tooltip hint doesn't appear for read-only spectators.
        const actor = new Building(tile, building, this.readOnly ? undefined : this._currentUser?.id, canDestroy);
        this._buildings.set(building.id, {
            actor,
            updatedAt: building.updatedAt,
            tile,
        });
        if (building.id === this._pendingFocusId) {
            this._pendingFocusId = null;
            this.focusBuilding(building.id);
        }
        const hasLink = !!(building.linkUrl && isHttpUrl(building.linkUrl));
        // preferLink lets Shift+Click open an owned manual company's link instead of destroying it
        // (canDestroy would otherwise always win) — no-ops back to the destroy path when there's no
        // link to open (e.g. a special building, which never has one).
        const runAction = (preferLink) => {
            if (canDestroy && (!preferLink || !hasLink)) {
                // The tap that opens the modal doesn't move the pointer off the building, so
                // pointerleave never fires — hide the hover tooltip explicitly or it renders on top.
                hideBuildingTooltip();
                showDestroyConfirm(building, () => this.destroyBuilding(building));
            }
            else if (hasLink) {
                playBuildingRedirect();
                window.open(building.linkUrl, "_blank", "noopener,noreferrer");
            }
        };
        const tap = createTapHandler((evt) => {
            evt.cancel();
            // Mobile has no hover: first tap opens an info overlay (BuildingTooltip's touch
            // equivalent) with the same action(s) moved into real buttons, instead of running one
            // immediately. Desktop keeps the original direct-click behavior — unless accessibility
            // mode is on, which opts desktop into the same tap-to-overlay model (see accessibility.ts).
            if (isMobile() || isAccessibilityMode()) {
                const actions = {};
                if (canDestroy) {
                    actions.onDestroy = () => {
                        hideBuildingInfoOverlay();
                        runAction(false);
                    };
                }
                if (hasLink) {
                    actions.onVisit = () => {
                        hideBuildingInfoOverlay();
                        runAction(true);
                    };
                }
                showBuildingInfoOverlay(evt.screenPos.x, evt.screenPos.y, building, actions);
                return;
            }
            const shiftHeld = evt.nativeEvent instanceof MouseEvent && evt.nativeEvent.shiftKey;
            runAction(shiftHeld);
        });
        actor.on("pointerdown", tap.down);
        actor.on("pointerup", tap.up);
        actor.on("pointercancel", tap.cancel);
        this.add(actor);
    }
    buildingsForUser(userId) {
        const owned = this.mapStore.buildingsOwnedBy(userId);
        return {
            city: owned.filter((b) => b.kind === "special"),
            company: owned.filter((b) => b.kind === "company"),
        };
    }
    showBuildingsPanelFor(userId, userName) {
        const { city, company } = this.buildingsForUser(userId);
        showUserBuildingsPanel(userName, city, company, (id) => this.focusBuilding(id));
    }
    static FOCUS_PAN_DURATION_MS = 600;
    // Called by UserBuildingsPanel row clicks (via the handler wiring above) to pan the camera to
    // a specific building and flash a temporary highlight at its tile. Silently no-ops if the
    // building isn't currently on the map (e.g. stale panel data after a destroy/SSE update).
    focusBuilding(id) {
        const entry = this._buildings.get(id);
        if (!entry)
            return;
        void this.camera.move(entry.tile.center, GameScene.FOCUS_PAN_DURATION_MS);
        new TileHighlight(this, entry.tile);
    }
    // Called via setOnCompanyCreated right after a manual company is created (AddCompanyModal) —
    // same pan/zoom/highlight as focusBuilding, but the server-picked tile means the actor usually
    // doesn't exist yet (it arrives over SSE after this call resolves), so this defers to
    // addBuildingActor via _pendingFocusId instead of no-op'ing like focusBuilding would.
    focusNewBuilding(id) {
        if (this._buildings.has(id)) {
            this.focusBuilding(id);
            return;
        }
        this._pendingFocusId = id;
    }
    async destroyBuilding(building) {
        const isManualCompany = building.kind === "company" && building.source === "manual";
        const path = isManualCompany
            ? `/api/companies/${building.id}`
            : `/api/build/${building.id}`;
        try {
            await apiDelete(path);
            posthog.capture(isManualCompany ? "manual_company_removed" : "special_building_removed");
            return { ok: true };
        }
        catch (e) {
            return {
                ok: false,
                error: e instanceof ApiError ? e.message : "Verbindungsfehler.",
            };
        }
    }
    setupBuildMode(engine) {
        this.cityMap.attachClickHandling(engine);
        setBuildHandler(() => {
            const state = this.mapStore.getState();
            if (!state || !this._currentUser)
                return;
            showBuildMenu(state, this._currentUser.id, (type) => {
                this._placing = type;
                setPlacementActive(true);
                const def = SPECIAL_BUILDINGS[type];
                const hint = def.placement === "water"
                    ? `Tippe auf ein Wasserfeld für: ${def.name}`
                    : undefined;
                showPlacementBanner(def.name, () => {
                    this._placing = null;
                    setPlacementActive(false);
                    hidePlacementBanner();
                    setCursor("idle");
                }, hint);
            });
        });
        this.cityMap.setOnTileClick((tile) => {
            if (!this._placing)
                return;
            void this.handleTilePlacement(tile);
        });
        // Hammer cursor previews where the currently-selected special building can legally land;
        // 'disabled' flags every other tile (occupied, out of bounds, wrong terrain) while placing.
        // This computes the hovered tile directly from the pointer position on every move instead
        // of going through tile pointerenter/pointerleave: those fire per-tile, and when the
        // pointer crosses from tile A straight into tile B, A's 'leave' and B's 'enter' both land
        // in the same dispatch pass with an order that depends on the tiles' array index (i.e. the
        // direction of movement) — whichever fires last wins, so 'leave' (which we'd have to treat
        // as "nothing hovered") could clobber a correct, freshly-entered valid tile roughly half
        // the time. Asking "what tile is here right now" directly sidesteps that race entirely.
        engine.input.pointers.on("move", (evt) => {
            const type = this._placing;
            if (!type)
                return;
            const tile = this.cityMap.isoMap.getTileByPoint(evt.worldPos);
            setCursor(tile && this.canPlaceAt(tile, type) ? "hammer" : "disabled");
        });
    }
    isOccupiedOrStreet(tile) {
        return (this.cityMap.isTileOccupied(tile) ||
            this.mapStore.getStreetKeys().has(`${tile.x},${tile.y}`));
    }
    isInBounds(tile) {
        return (Math.abs(tile.x - MAP_CENTER) <= this._mapRadius &&
            Math.abs(tile.y - MAP_CENTER) <= this._mapRadius);
    }
    matchesTerrain(tile, type) {
        const def = SPECIAL_BUILDINGS[type];
        return def.placement === "land"
            ? !TERRAIN.blocksBuilding(tile.x, tile.y)
            : TERRAIN.isWater(tile.x, tile.y);
    }
    // Client-side placement preview — mirrors the server's rules 2/3. Reachability is not
    // pre-checked client-side; the server answers with a clear 422 instead of duplicating the BFS here.
    canPlaceAt(tile, type) {
        return (!this.isOccupiedOrStreet(tile) &&
            this.isInBounds(tile) &&
            this.matchesTerrain(tile, type));
    }
    async handleTilePlacement(tile) {
        const type = this._placing;
        if (!type)
            return;
        const screenPos = this.engine.screen.worldToScreenCoordinates(tile.center);
        const showError = (msg) => showPlacementErrorToast(screenPos.x, screenPos.y, msg);
        if (this.isOccupiedOrStreet(tile)) {
            showError(PLACEMENT_ERRORS.occupied);
            return;
        }
        if (!this.isInBounds(tile)) {
            showError(PLACEMENT_ERRORS.outOfBounds);
            return;
        }
        if (!this.matchesTerrain(tile, type)) {
            const def = SPECIAL_BUILDINGS[type];
            showError(def.placement === "land"
                ? PLACEMENT_ERRORS.wrongTerrainLand
                : PLACEMENT_ERRORS.wrongTerrainWater);
            return;
        }
        try {
            await apiPost("/api/build", {
                buildingType: type,
                tileX: tile.x,
                tileY: tile.y,
            });
            this._placing = null;
            setPlacementActive(false);
            hidePlacementBanner();
            setCursor("idle");
            posthog.capture("special_building_placed", {
                building_type: type,
                placement: SPECIAL_BUILDINGS[type].placement,
            });
            playBuildingPlaced();
            this.spawnPendingDust(tile);
        }
        catch (e) {
            showError(e instanceof ApiError ? e.message : "Verbindungsfehler.");
        }
    }
    // Marks a tile as under construction until its building arrives via SSE (or the fallback
    // fires) — see resolvePendingDust and DUST_MIN_VISIBLE_MS/DUST_FALLBACK_MS above.
    spawnPendingDust(tile) {
        const key = `${tile.x},${tile.y}`;
        this._pendingDust.get(key)?.effect.stop();
        const effect = new BuildDustEffect(this, tile);
        const fallback = setTimeout(() => {
            this._pendingDust.delete(key);
            effect.stop();
        }, DUST_FALLBACK_MS);
        this._pendingDust.set(key, { effect, placedAt: Date.now(), fallback });
    }
    resolvePendingDust(tileX, tileY) {
        const key = `${tileX},${tileY}`;
        const pending = this._pendingDust.get(key);
        if (!pending)
            return;
        this._pendingDust.delete(key);
        clearTimeout(pending.fallback);
        const remaining = DUST_MIN_VISIBLE_MS - (Date.now() - pending.placedAt);
        setTimeout(() => pending.effect.stop(), Math.max(0, remaining));
    }
    setupCameraControls(engine) {
        this._viewBounds = this.worldBoundsForRadius(START_RADIUS);
        this._minZoom = this.calcMinZoom(engine);
        this.camera.zoom = Math.max(this._minZoom, 1);
        engine.screen.events.on("resize", () => {
            this._minZoom = this.calcMinZoom(engine);
            if (this.camera.zoom < this._minZoom) {
                this.camera.zoom = this._minZoom;
            }
        });
        this.camera.addStrategy({
            target: null,
            action: (_target, cam, eng) => {
                const { left, right, top, bottom } = this._viewBounds;
                const zoom = cam.zoom;
                const vw = eng.screen.drawWidth / zoom;
                const vh = eng.screen.drawHeight / zoom;
                const bufX = BUF_FRAC * vw;
                const bufY = BUF_FRAC * vh;
                const xMin = left - bufX + vw / 2;
                const xMax = right + bufX - vw / 2;
                const yMin = top - bufY + vh / 2;
                const yMax = bottom + bufY - vh / 2;
                const finalX = xMax >= xMin ? Math.max(xMin, Math.min(xMax, cam.pos.x)) : 0;
                const finalY = yMax >= yMin
                    ? Math.max(yMin, Math.min(yMax, cam.pos.y))
                    : (top + bottom) / 2;
                return ex.vec(finalX, finalY);
            },
        });
        engine.input.pointers.on("down", (evt) => {
            // Dismiss the mobile info overlay on every new pointerdown — whether it lands on empty
            // map (tap outside) or is the start of a drag (panning); a genuine tap on a building
            // re-opens it (possibly for a different building) once the matching pointerup fires below.
            hideBuildingInfoOverlay();
            this.activePointers.set(evt.pointerId, {
                startPos: evt.screenPos.clone(),
                lastPos: evt.screenPos.clone(),
                isDragging: false,
            });
        });
        engine.input.pointers.on("move", (evt) => {
            const data = this.activePointers.get(evt.pointerId);
            if (data) {
                const currentPos = evt.screenPos;
                if (this.activePointers.size === 1) {
                    if (evt.pointerType === ex.PointerType.Mouse &&
                        !engine.input.pointers.isDown(evt.pointerId)) {
                        this.activePointers.delete(evt.pointerId);
                        return;
                    }
                    if (!data.isDragging &&
                        currentPos.distance(data.startPos) > TAP_SLOP_PX) {
                        data.isDragging = true;
                        setCursor("grabbing");
                    }
                    if (data.isDragging) {
                        const deltaScreen = currentPos.sub(data.lastPos);
                        this.camera.pos = this.camera.pos.sub(deltaScreen.scale(1 / this.camera.zoom));
                    }
                }
                else if (this.activePointers.size === 2) {
                    const iter = this.activePointers.entries();
                    const [id1, d1] = iter.next().value;
                    const [id2, d2] = iter.next().value;
                    const p1 = evt.pointerId === id1 ? currentPos : d1.lastPos;
                    const p2 = evt.pointerId === id2 ? currentPos : d2.lastPos;
                    const oldP1 = d1.lastPos;
                    const oldP2 = d2.lastPos;
                    const oldDist = oldP1.distance(oldP2);
                    const newDist = p1.distance(p2);
                    if (oldDist > 0 && newDist > 0) {
                        const zoomFactor = newDist / oldDist;
                        const newZoom = Math.max(this._minZoom, Math.min(2, this.camera.zoom * zoomFactor));
                        this.camera.zoom = newZoom;
                    }
                }
                data.lastPos = currentPos.clone();
            }
        });
        engine.input.pointers.on("up", (evt) => {
            this.activePointers.delete(evt.pointerId);
            // Only reset once every pointer is up — releasing one finger of a two-finger pinch
            // shouldn't snap the cursor back while the other finger keeps the gesture going.
            if (this.activePointers.size === 0)
                setCursor("idle");
        });
        engine.input.pointers.primary.on("wheel", (evt) => {
            const nativeWheel = evt.ev;
            if (nativeWheel.ctrlKey) {
                const zoomSpeed = 0.001;
                const newZoom = Math.max(this._minZoom, Math.min(2, this.camera.zoom - evt.deltaY * zoomSpeed));
                this.camera.zoom = newZoom;
            }
            else {
                this.camera.pos.x += evt.deltaX / this.camera.zoom;
                this.camera.pos.y += evt.deltaY / this.camera.zoom;
            }
        });
    }
}
